import dayjs from "dayjs";
import * as XLSX from "xlsx";
import * as XLSXSH from "sheetjs-style";
import { saveAs } from "file-saver";
import {
  DATA_OPS_ASSIGNEE_ROLE_IDS,
  fetchDataOpsAssigneeUsers,
} from "../../../../actions/qc";

export { DATA_OPS_ASSIGNEE_ROLE_IDS, fetchDataOpsAssigneeUsers };

/**
 * Shared Data Ops Analytics helpers used across multiple tabs/components.
 * Keep single-use helpers next to their consumer, not here.
 */

/** Field tokens for DataOpsFilters (Overdue / Retest / RCA). */
export const filterField = {
  dateRange: "dateRange",
  status: "status",
  testCategory: "testCategory",
  test: "test",
  customer: "customer",
  search: "search",
  createdBy: "createdBy",
  overdueReason: "overdueReason",
  retestReason: "retestReason",
  retest: "retest",
  subscriptionOnly: "subscriptionOnly",
  assignTo: "assignTo",
  export: "export",
};

/** Catalog tokens for DataOpsFilters option loading. */
export const filterCatalog = {
  categories: "categories",
  customers: "customers",
  users: "users",
  dataOpsAssignees: "dataOpsAssignees",
  overdueReasons: "overdueReasons",
  retestReasons: "retestReasons",
};

export const overdueFilterFields = [
  filterField.search,
  filterField.dateRange,
  filterField.testCategory,
  filterField.test,
  filterField.customer,
  filterField.overdueReason,
  filterField.retest,
  filterField.subscriptionOnly,
  filterField.export,
];

export const overdueFilterCatalogs = [
  filterCatalog.categories,
  filterCatalog.customers,
  filterCatalog.overdueReasons,
];

export const retestFilterFields = [
  filterField.search,
  filterField.dateRange,
  filterField.testCategory,
  filterField.test,
  filterField.customer,
  filterField.createdBy,
  filterField.subscriptionOnly,
  filterField.export,
];

export const retestFilterCatalogs = [
  filterCatalog.categories,
  filterCatalog.customers,
  filterCatalog.users,
];

export const rcaFilterFields = [
  filterField.search,
  filterField.dateRange,
  filterField.status,
  filterField.assignTo,
  filterField.testCategory,
  filterField.test,
  filterField.customer,
  filterField.createdBy,
  filterField.retestReason,
  filterField.export,
];

export const rcaFilterCatalogs = [
  filterCatalog.categories,
  filterCatalog.customers,
  filterCatalog.users,
  filterCatalog.dataOpsAssignees,
  filterCatalog.retestReasons,
];

/**
 * Default date range for Data Ops Analytics: last 1 month through today.
 * Returns Ant Design RangePicker-compatible dayjs values.
 */
export const getDefaultLastOneMonthDateRange = () => [
  dayjs().subtract(1, "month"),
  dayjs(),
];

/** Last 7 days through today (Add RCA for Data Ops RCA RCA eligible tests). */
export const getDefaultLastOneWeekDateRange = () => [
  dayjs().subtract(1, "week"),
  dayjs(),
];

/**
 * Shared table layout for Overdue + Retest + RCA so tabs stay visually consistent.
 */

export const DATA_OPS_TABLE_SCROLL_Y = "60vh";

export const DATA_OPS_TABLE_PAGINATION = {
  defaultPageSize: 50,
  showSizeChanger: true,
  pageSizeOptions: ["25", "50", "100"],
};

/**
 * Sum explicit column widths for Ant Design scroll.x.
 * Prefer this over "max-content" so declared widths are respected.
 * @param {{ width?: number }[]} columns
 * @returns {number}
 */
export const sumColumnWidths = (columns = []) => (
  columns.reduce((total, column) => total + (column.width || 0), 0)
);

/**
 * Shared Excel download for Data Ops Analytics tabs.
 * Empty / null cell values stay blank (no "-" placeholder).
 */
export const downloadDataOpsExcel = ({
  rows,
  columns,
  fileName,
  sheetName = "Export",
  columnWidth = 22,
}) => {
  const headerRow = columns.map((column) => column.header);
  const dataRows = rows.map((row) => (
    columns.map((column) => {
      const cellValue = row[column.key];
      return cellValue == null || cellValue === "" || cellValue === "-"
        ? ""
        : cellValue;
    })
  ));
  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
  worksheet["!cols"] = columns.map(() => ({ wch: columnWidth }));

  columns.forEach((_, columnIndex) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: columnIndex });
    if (worksheet[cellRef]) {
      worksheet[cellRef].s = { font: { bold: true } };
    }
  });

  const workbook = {
    Sheets: { [sheetName]: worksheet },
    SheetNames: [sheetName],
  };
  const buffer = XLSXSH.write(workbook, {
    bookType: "xlsx",
    type: "array",
    cellStyles: "true",
  });
  saveAs(
    new Blob(
      [buffer],
      { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" },
    ),
    fileName,
  );
};

export const buildDataOpsExportFileName = (prefix, scopeLabel = "") => {
  const timestamp = dayjs().format("MM/DD/YYYY_HHmmss");
  const safePrefix = String(prefix)
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    || "DataOps";
  const safeScope = String(scopeLabel)
    .trim()
    .replace(/[^\w-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);

  if (safeScope) {
    return `${safePrefix}_${safeScope}_${timestamp}.xlsx`;
  }
  return `${safePrefix}_Export_${timestamp}.xlsx`;
};

const getUserRoleId = (user = {}) => {
  const role = user.role_id ?? user.role;
  if (role == null || role === "") {
    return null;
  }
  return Number(role);
};

/**
 * Map /users/ results to Ant Select options for Data Ops Assign To.
 * Keeps only DATA_OPS_ASSIGNEE_ROLE_IDS (safety net if API returns extras).
 */
export const mapDataOpsAssigneeSelectOptions = (results = []) => {
  const allowedRoleIds = new Set(DATA_OPS_ASSIGNEE_ROLE_IDS);
  return results
    .filter((user) => {
      const roleId = getUserRoleId(user);
      return roleId != null && allowedRoleIds.has(roleId);
    })
    .slice()
    .sort((userA, userB) => {
      const nameA = `${userA.first_name} ${userA.last_name}`.trim().toLowerCase();
      const nameB = `${userB.first_name} ${userB.last_name}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    })
    .map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`.trim(),
    }));
};

/** Load + map Assign To options for Data Ops RCA Analytics / RCA tabs. */
export const loadDataOpsAssigneeSelectOptions = async () => {
  const response = await fetchDataOpsAssigneeUsers();
  if (!response.success) {
    return {
      success: false,
      message: response.message || "Failed to load Data Ops assignee users.",
      options: [],
    };
  }
  return {
    success: true,
    options: mapDataOpsAssigneeSelectOptions(response.results || []),
  };
};
